import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useProjectDetails,
  useProjectRepos,
  useProjectContracts,
  useProjectNews,
} from '../lib/hooks/useProjectDetails';
import { ProjectHeader } from '../components/ProjectHeader';
import { ProjectStats } from '../components/ProjectStats';
import { RepoCard } from '../components/RepoCard';
import { ContractCard } from '../components/ContractCard';
import { DataQualityBadge } from '../components/DataQualityBadge';

type Tab = 'repos' | 'contracts' | 'news';

export function ProjectDetailPageNew() {
  const { symbol } = useParams<{ symbol: string }>();
  const [activeTab, setActiveTab] = useState<Tab>('repos');

  const {
    data: projectData,
    isLoading: projectLoading,
    error: projectError,
    enrichmentQuality,
    isEnriching,
  } = useProjectDetails(symbol!);
  const { data: reposData, isLoading: reposLoading } = useProjectRepos(symbol!, {
    limit: 20,
    sort: 'stars',
    order: 'desc',
  });
  const { data: contractsData, isLoading: contractsLoading } = useProjectContracts(symbol!);
  const { data: newsData, isLoading: newsLoading } = useProjectNews(symbol!, 20, 0);

  if (!symbol) {
    return (
      <div className="min-h-screen bg-cambrian-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-cambrian-text">No project specified</h1>
          <p className="text-gray-600 mt-2">Please provide a project symbol</p>
        </div>
      </div>
    );
  }

  if (projectLoading) {
    return (
      <div className="min-h-screen bg-cambrian-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cambrian-primary mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading project...</p>
        </div>
      </div>
    );
  }

  if (projectError) {
    return (
      <div className="min-h-screen bg-cambrian-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Error loading project</h1>
          <p className="text-gray-600 mt-2">{(projectError as Error).message}</p>
        </div>
      </div>
    );
  }

  if (!projectData) {
    return (
      <div className="min-h-screen bg-cambrian-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-cambrian-text">Project not found</h1>
          <p className="text-gray-600 mt-2">The project "{symbol}" could not be found</p>
        </div>
      </div>
    );
  }

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'repos', label: 'Repositories', count: projectData.stats.totalRepos },
    { id: 'contracts', label: 'Contracts', count: projectData.stats.totalContracts },
    { id: 'news', label: 'News', count: projectData.stats.newsStories },
  ];

  return (
    <div className="min-h-screen bg-cambrian-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ProjectHeader project={projectData.project} />

        {/* Data Quality Indicator */}
        {(enrichmentQuality || isEnriching) && (
          <div className="mb-4 flex justify-end">
            <DataQualityBadge quality={enrichmentQuality} isEnriching={isEnriching} />
          </div>
        )}

        <ProjectStats stats={projectData.stats} />

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-elevation-medium mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    py-4 px-6 text-sm font-medium border-b-2 transition-colors
                    ${
                      activeTab === tab.id
                        ? 'border-cambrian-primary text-cambrian-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className="ml-2 py-0.5 px-2 rounded-full bg-gray-100 text-xs">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'repos' && (
              <div>
                {reposLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cambrian-primary mx-auto"></div>
                    <p className="text-gray-600 mt-4">Loading repositories...</p>
                  </div>
                ) : reposData && reposData.repositories.length > 0 ? (
                  <div className="space-y-4">
                    {reposData.repositories.map((repo) => (
                      <RepoCard key={repo.url} repo={repo} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p className="text-gray-600">No repositories found</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'contracts' && (
              <div>
                {contractsLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cambrian-primary mx-auto"></div>
                    <p className="text-gray-600 mt-4">Loading contracts...</p>
                  </div>
                ) : contractsData && Object.keys(contractsData.contractsByChain).length > 0 ? (
                  <div className="space-y-6">
                    {Object.entries(contractsData.contractsByChain).map(([chain, contracts]) => (
                      <div key={chain}>
                        <h3 className="text-lg font-semibold text-cambrian-text mb-3 capitalize">
                          {chain} ({contracts.length})
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {contracts.map((contract) => (
                            <ContractCard
                              key={`${contract.chain}-${contract.address}`}
                              contract={contract}
                              chain={chain}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-gray-600">No contracts found</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'news' && (
              <div>
                {newsLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cambrian-primary mx-auto"></div>
                    <p className="text-gray-600 mt-4">Loading news...</p>
                  </div>
                ) : newsData && newsData.stories.length > 0 ? (
                  <div className="space-y-4">
                    {newsData.stories.map((story) => (
                      <div
                        key={story.storyId}
                        className="bg-white rounded-lg shadow-elevation-low hover:shadow-elevation-medium transition-shadow p-5 border border-gray-100"
                      >
                        <h4 className="font-semibold text-cambrian-text mb-2">{story.title}</h4>
                        {story.description && (
                          <p className="text-sm text-gray-600 mb-3">{story.description}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-3 text-xs">
                          <span
                            className={`px-2 py-1 rounded-full font-medium ${
                              story.sentiment === 'positive'
                                ? 'bg-green-100 text-green-800'
                                : story.sentiment === 'negative'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {story.sentiment}
                          </span>
                          <span className="text-gray-500">
                            Confidence: {Math.round(story.confidenceScore * 100)}%
                          </span>
                          <span className="text-gray-500">
                            {new Date(story.indexedAt).toLocaleDateString()}
                          </span>
                          {story.websiteUrl && (
                            <a
                              href={story.websiteUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-cambrian-primary hover:text-cambrian-primary-dark"
                            >
                              Read more →
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                    </svg>
                    <p className="text-gray-600">No news stories found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
